import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { stack } from '@flipster/styled-system/patterns';

import { CopyTradingCopyModalSegmentView, copyTradingCopyModalSegmentBunja } from './event';
import { HeaderSection } from './section/1-header';
import { ExecutionModeSection } from './section/2-execution-mode';
import { CopyAmountSection } from './section/3-copy-amount';
import { PositionCopyModeSection } from './section/4-position-copy-mode';
import { CopyMultiplierSection } from './section/5-copy-multiplier';
import { TotalStopLossSection } from './section/6-stop-loss';
import { MaxEntrySlippageSection } from './section/7-max-entry-slippage';
import { AgreeTncSection } from './section/8-agree-tnc';
import { AvailableAmountLimitWarning } from './section/available-amount-limit-warning';
import { CopyTradePortfolioIdContext, copyTradingSettingsBunja } from './state';

interface CopyTradingSettingsModalProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const CopyTradingSettingsModal = ({ id, isOpen, onClose, onExit }: CopyTradingSettingsModalProps) => {
  return (
    <CopyTradePortfolioIdContext value={id}>
      <CopyTradingSettingsModalImpl isOpen={isOpen} onClose={onClose} onExit={onExit} />
    </CopyTradePortfolioIdContext>
  );
};

interface CopyTradingSettingsModalImplProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const CopyTradingSettingsModalImpl = ({ isOpen, onClose, onExit }: CopyTradingSettingsModalImplProps) => {
  const { portfolioAtom, submitCopyPortfolio, disabledAtom } = useBunja(copyTradingSettingsBunja);
  const { trackCopyTradingCopyConfirmClick } = useBunja(copyTradingCopyModalSegmentBunja);
  const portfolio = useAtomValue(portfolioAtom);
  const disabled = useAtomValue(disabledAtom);

  return (
    <>
      <CopyTradingCopyModalSegmentView />
      <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
        <Modal.Header>
          <Modal.Title label={<FormattedMessage id='Copy.r251107.Copy_trading_settings' />} />
        </Modal.Header>
        <Modal.Content isLoading={!portfolio} css={stack.raw({ gap: 16, pt: 16 })}>
          <AvailableAmountLimitWarning />
          <HeaderSection />
          <ExecutionModeSection />
          <div className={stack({ gap: 24 })}>
            <CopyAmountSection onClose={onClose} />
            <PositionCopyModeSection />
            <CopyMultiplierSection />
            <TotalStopLossSection />
            <MaxEntrySlippageSection />
            <AgreeTncSection onClose={onClose} />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            withSpinner
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            onClick={async () => {
              trackCopyTradingCopyConfirmClick();
              await submitCopyPortfolio();
              onClose();
            }}
            disabled={disabled}
          />
        </Modal.Footer>
      </Modal.Root>
    </>
  );
};
